import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Languages, Users, ShieldCheck, MessageSquareText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const HERO_IMG =
  "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwb2ZmaWNlfGVufDB8fHx8MTc4Njg5NzcwOXww&ixlib=rb-4.1.0&q=85";
const NET_IMG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG5ldHdvcmslMjBjb25uZWN0aW9uc3xlbnwwfHx8fDE3ODY4OTc3MDh8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const go = () => navigate(user ? "/uygulama" : "/giris");

  return (
    <div className="relative z-10 min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-heading text-2xl font-black tracking-tighter text-primary" data-testid="brand-logo">
            alignee
          </span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/giris" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-login-link">
              {t("nav.login")}
            </Link>
            <Button onClick={go} className="rounded-full hover:-translate-y-0.5 transition-transform" data-testid="nav-cta-btn">
              {t("nav.cta")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <Languages className="w-3.5 h-3.5" /> {t("hero.badge")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            {t("hero.title1")}<br />
            <span className="text-primary">{t("hero.title2")}</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
            {t("hero.desc")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={go} className="rounded-full text-base h-12 px-7 hover:-translate-y-0.5 transition-transform group" data-testid="hero-cta-btn">
              {t("hero.cta")}
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/giris")} className="rounded-full text-base h-12 px-7" data-testid="hero-secondary-btn">
              {t("hero.secondary")}
            </Button>
          </div>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -inset-2 sm:-inset-4 bg-primary/5 rounded-2xl sm:rotate-2" />
          <img src={HERO_IMG} alt="Team collaboration" className="relative rounded-xl border border-border object-cover w-full h-[420px] shadow-sm" />
          <div className="mt-4 sm:mt-0 sm:absolute sm:-bottom-5 sm:-left-5 bg-card border border-border rounded-lg p-4 shadow-lg w-full sm:max-w-[240px]" data-testid="hero-translation-card">
            <p className="text-xs text-muted-foreground mb-1">{t("demo.devWrote")}</p>
            <p className="text-sm font-medium">{t("demo.devMsg")}</p>
            <div className="h-px bg-border my-2" />
            <p className="text-xs text-primary font-semibold mb-1">{t("demo.hrRead")}</p>
            <p className="text-sm">{t("demo.hrMsg")}</p>
          </div>
        </div>
      </section>

      {/* Features tetris grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={Languages} title={t("feat.translateTitle")} desc={t("feat.translateDesc")} span="md:col-span-2" />
          <Feature icon={ShieldCheck} title={t("feat.tenantTitle")} desc={t("feat.tenantDesc")} />
          <Feature icon={Users} title={t("feat.rolesTitle")} desc={t("feat.rolesDesc")} />
          <Feature icon={MessageSquareText} title={t("feat.channelsTitle")} desc={t("feat.channelsDesc")} span="md:col-span-2" />
        </div>
      </section>

      {/* Banner */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative rounded-2xl overflow-hidden border border-border">
          <img src={NET_IMG} alt="Network" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-primary/85" />
          <div className="relative p-10 lg:p-16 text-primary-foreground">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-lg">
              {t("banner.title")}
            </h2>
            <Button size="lg" onClick={go} variant="secondary" className="mt-8 rounded-full h-12 px-7 text-base hover:-translate-y-0.5 transition-transform" data-testid="banner-cta-btn">
              {t("banner.cta")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <span className="font-heading font-bold text-primary">alignee</span>
          <span>{t("common.copyright")}</span>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, desc, span = "" }) {
  return (
    <div className={`${span} group border border-border rounded-xl p-7 bg-card hover:border-primary/40 transition-colors`}>
      <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5 group-hover:scale-105 transition-transform">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-heading text-xl font-semibold tracking-tight mb-2">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}
